import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from 'axios';
import { createTransactionID, getRiyadhISOString } from 'mobily-ui-library/utils';
import { useGlobalStore } from '../stores/globalStore';
import endpoints from './endpoints.ts';


interface WindowEnv {
  apigee_bm_base_url?: string;
  api_base_url?: string;
  x_api_key?: string;
  apigee_bm_x_api_key?: string;
  clientToken?: string;
  lang?: string;
  cem_key?: string;
  [key: string]: unknown;
}

const win = window as unknown as WindowEnv;

export const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.baseURL = win.apigee_bm_base_url ?? win.api_base_url ?? '';
  const store = useGlobalStore.getState();
  const apiKey = store.apiKey ?? (win.x_api_key as string | undefined) ?? (win.apigee_bm_x_api_key as string | undefined);
  const token = store.token;
  const cemKey = (win.cem_key as string | undefined);
  const headers = (config.headers ?? {}) as AxiosRequestHeaders;
  if (apiKey) headers['x-api-key'] = String(apiKey);
  if (token) headers.Authorization = `Bearer ${token}`;
  if (cemKey) headers['cem-key'] = String(cemKey);

  let correlationId = store.correlationId;

  if(!store.correlationId){
    correlationId = createTransactionID(true);
    store.setCorrelationId(correlationId);
  }
  headers['X-Correlation-Id'] = correlationId;
  headers['X-Transaction-Id'] = createTransactionID();
  headers['X-Source-Channel'] = 'PORTAL';
  headers['X-Journey-Name'] = 'FTTH_FTTR_LEAD';
  headers['X-Timestamp'] = getRiyadhISOString();
  config.headers = headers;

  return config;
});

api.interceptors.response.use(
  (res: AxiosResponse) => res,
  (err: AxiosError | unknown) => {
    return Promise.reject(err);
  }
);

export const fetcher = <T = unknown>(url: string, { method, body }: { method: 'get' | 'post' | 'put' | 'delete'; body?: any }) => api[method]<T>(url, { method, body }).then((r: AxiosResponse<T>) => r.data as T);

export const getProductOfferByID = (productId: string) => {
  const query =
    `{
    getProductOfferByID(id: "`+ productId + `") {
      id{
        key
        version
      }
      attachment {
        url
        name
      }
      name
      createdBy
      createdOn
          externalID
      productOfferingTerm{
          duration{
              amount
              units
          }
      }
      metaExtension {
        dataSet {
          genericData {
            schemaRef
            valueSet
          }
        }
      }
      productSpecification{
          id
          name
      }
      specCharValueUse{
        name
        specCharacteristicValue{
          value
          unitOfMeasure
          isDefault
        }

      }
    category {
        id
        name
        type
        isAnchor
      }
      productOfferingPrice{
        id
        code
        name
        externalID
        processContext{
          id
          name
        }
        priceType
        price{
          unit
          value
        }
      }
      offerLabels{
          name
          description
          channel
      }
    }
  }`;
  return api.post(endpoints.QUERY_GRAPHQL, { query: query });
}
export const bffLoginInitiateOtp = ({ number, lang, customerName }: { number: any, lang: any, customerName: any }) => {
  const data = {
    version: "v1",
    journey: "DEVICES",
    operation: "SEND_OTP",
    language: lang,
    extraInfo: [
      {
        key: "ACCOUNT_NUMBER",
        value: number
      },
      {
        key: "CUSTOMER_NAME",
        value: customerName
      },
      {
        "key": "OTP_LENGTH",
        "value": '4',
      },
      {
        "key": "IS_NUMERIC",
        "value": 'Y'
      },
    ]
  }
  return api.post(endpoints.BFF_LOGIN_INITIATE_OTP, data);
}

export const bffValidateOtp = ({ otp, refId }: { otp: any, refId: any }) => {
  const data = {
    operation: "VALIDATE_OTP",
    extraInfo: [
      {
        key: "REF_ID",
        value: refId
      },
      {
        key: "OTP",
        value: otp
      }
    ]
  }
  return api.put(endpoints.BFF_LOGIN_VALIDATE_OTP, data);
}

export const bffResendOtp = ({ refId }: { refId: any }) => {
  const data = {
    operation: "RESEND_OTP",
    extraInfo: [
      {
        key: "REF_ID",
        value: refId
      },
    ]
  }
  return api.put(endpoints.BFF_LOGIN_RESEND_OTP, data);
}
export const createShoppingCart = ({ ProductOffer, productId, specialApps }: { ProductOffer: any, productId: any, specialApps: any }) => {
  const journey = sessionStorage.getItem('journey') ?? '';
  const productOfferingTerm = ProductOffer?.productOfferingTerm;
  let itemTerm = productOfferingTerm?.[0];

  const contractLength = journey.split('_');
  if (contractLength[0] === 'FTTH' && contractLength.length > 1) {
    itemTerm = productOfferingTerm?.filter((po: any) => {
      const term = po?.duration;
      return contractLength[1] === `${term?.amount}:${term?.units}`;
    }) ?? itemTerm;
  }

  const productCharacteristics = ProductOffer?.specCharValueUse;
  const productCharacteristic = [];

  for (let i = 0; i < productCharacteristics.length; i++) {
    if (productCharacteristics[i].name === 'SIM Type') {
      const defaultSimType = productCharacteristics[i].specCharacteristicValue.filter((sc: any) => sc?.isDefault === true)[0];
      if (defaultSimType) {
        productCharacteristic.push({ name: productCharacteristics[i].name, value: defaultSimType?.value });
      } else {
        productCharacteristic.push({ name: productCharacteristics[i].name, value: 'Not Required' });
      }
    } else if (productCharacteristics[i].name === 'Social Media Apps Code') {
      productCharacteristic.push({ name: "Social Media Apps Code", value: specialApps });
    } else {
      productCharacteristic.push({ name: productCharacteristics[i].name, value: productCharacteristics[i].specCharacteristicValue?.[0]?.value })
    }
  }

  const data = {
    channel: {
      "id": "bo_lov_portals",
      "name": "Portals"
    },
    offerContainer: [{
      additionalAttributes: [
        {
          "name": "orderType",
          "value": "FTTH"
        },
        {
          "name": "leadType",
          "value": "FTTH-FTTR-PKGS"
        }],
      cartItem: [{
        action: 'Add',
        productOffering: {
          id: productId,
          name: ProductOffer?.name
        },
        product: {
          productCharacteristic: productCharacteristic
        },
        itemTerm: itemTerm,
        quantity: 1
      }],
      category: {
        id: 'home_internet_-pc_fb4tm_v1',
        name: 'Home Internet'
      },
      processContext: 'ACQ'
    }]
  }

  return api.post(endpoints.SHOPPING_CART+'?isNonPersistent=false', data);
}
export const updateAndValidateShoppingCart = ({ id }: { id: any }) => {
  const data: any = {
    ShoppingCartId: id,
    JourneyName: "HomeFiber"
  }
  return api.post(`${endpoints.UPDATE_VALIDATE_SHOPPING_CART}`, data);
}
export const captureData = ({ shoppingCart, capturedSections }: { shoppingCart: any, capturedSections: any }) => {
  const data: any = {
    context: {
      channel: {
        id: "bo_lov_portals",
        name: "Portals",  
      },
      serviceability: {},
      shoppingCart: shoppingCart,
    },
    sections: capturedSections
  }
  return api.post(endpoints.CAPTURE_DATA, data);
}
export const postProductOrder = ({ scId, otpReferenceId }: { scId: string, otpReferenceId: string }) => {
  const data = {
    shoppingCartId: scId,
    otpReferenceId
  }
  return api.post(endpoints.POST_PRODUCT_ORDER, data);
}

export const getStructure = ({ shoppingCart } : any) => {

  const data = {
    context: {
      channel: {
        id: "bo_lov_portals",
        name: "Portals",  
      },
      serviceability: {},
      shoppingCart: shoppingCart
    }
  }
  return api.post(endpoints.CHECKOUT_STRUCTURE, data);
}
export const getMainAndAroundODB = ({lat, lng, range} : {
  lat: any, 
  lng: any, 
  range?: any
}) => {
  const data = {
    "latitude": lat,
    "longitude": lng,
    "range": range || 100
  }

  return api.post(endpoints.GET_MAIN_AND_AROUND_ODB, data, {
    headers: {
      "x-channel-id": "PORTAL"
    }
  });
}
export const qualifyProductOffers = ({ shoppingCart, ocId }: {
  shoppingCart: any;
  ocId: string;
}) => {
  const data = {
    channel: {
      id: "bo_lov_portals",
      name: "Portals"
    },
    context: {
      additionalAttributes: [],
      shoppingCart
    },
    description: "Qualification",
    instantSyncQualification: true,
    productOfferingQualificationItemContainer: [
      {
        category: {
          id: "home_internet_-pc_fb4tm_v1",
          name: "Home Internet"
        },
        id: ocId,
        processContext: "ACQ",
        productOfferingQualificationItem: []
      }
    ],
    provideAlternative: true,
    provideUnavailabilityReason: true
  };

  return api.post(endpoints.QUALIFYING_PRODUCT_OFFERS, data);
}
export const addMeshExtenderToCart = ({ scId, ocId, productId, productName, quantity }: {
  scId: string;
  ocId: string;
  productId: string;
  productName: string;
  quantity: number;
}) => {
  const data = {
    cartItem: [
      {
        action: "Add",
        productOffering: {
          id: productId,
          name: productName
        },
        quantity
      }
    ]
  };
  return api.post(endpoints.ADD_TO_CART(scId, ocId), data);
}